import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { TypeAheadOption } from '../components/ui/typeahead';
// Master types that have under 50 values and can be cached
const CACHEABLE_MASTERS = [
  'product_category', // sku_sub_category
  'pack_form',
  'consume_type',
  'unit_type',
  'therapeutic_usage'
] as const;

type CacheableMaster = typeof CACHEABLE_MASTERS[number];

// Fetch all data for a specific master type
const fetchMasterData = async (master: CacheableMaster): Promise<TypeAheadOption[]> => {

  try {
    const response = await api.getUpmMasters(master, '');
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching ${master} data:`, error);
    return [];
  }
};

// Custom hook for each master type
export const useProductCategories = () => {
  return useQuery({
    queryKey: ['upm-masters', 'product_category'],
    queryFn: () => fetchMasterData('product_category'),
    staleTime: 1000 * 60 * 60, // 1 hour - data rarely changes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};

export const usePackForms = () => {
  return useQuery({
    queryKey: ['upm-masters', 'pack_form'],
    queryFn: () => fetchMasterData('pack_form'),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};

export const useConsumeTypes = () => {
  return useQuery({
    queryKey: ['upm-masters', 'consume_type'],
    queryFn: () => fetchMasterData('consume_type'),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};

export const useUnitTypes = () => {
  return useQuery({
    queryKey: ['upm-masters', 'unit_type'],
    queryFn: () => fetchMasterData('unit_type'),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};

export const useTherapeuticUsages = () => {
  return useQuery({
    queryKey: ['upm-masters', 'therapeutic_usage'],
    queryFn: () => fetchMasterData('therapeutic_usage'),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};

// UPM Variant Categories Hook
export const useVariantCategories = () => {
  return useQuery({
    queryKey: ['upm-variant-categories'],
    queryFn: async () => {
      try {
        const response = await api.getUpmVariantCategories();
        return response.data || [];
      } catch (error) {
        console.error('Error fetching variant categories:', error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
  });
};


// Utility function to filter options based on search query
export const filterOptions = (options: TypeAheadOption[], query: string, displayKey: string = 'name'): TypeAheadOption[] => {


  if (!query || query.length < 1) return options;

  const lowercaseQuery = query.toLowerCase();
  return options.filter(option =>
    option[displayKey]?.toLowerCase().includes(lowercaseQuery)
  );
};

// Cached search function for typeaheads
const searchUpmMastersCached = async (master: string, query: string): Promise<TypeAheadOption[]> => {

  try {
    if (master === 'composition') {
      const response = await api.getUpmMoleculeCompositionMasters(query);
      return response.data || [];
    }
    const response = await api.getUpmMasters(master, query);
    return response.data || [];
  } catch (error) {
    console.error(`Error searching UPM masters for ${master}:`, error);
    return [];
  }
};

// Hook for cached typeahead searches
export const useCachedTypeaheadSearch = (master: string, query: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['upm-masters-search', master, query],
    queryFn: () => searchUpmMastersCached(master, query),
    enabled: enabled && query.length >= 1, // Only search when query is at least 1 character
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Hook for UPM Tags search
export const useUpmTags = (query: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['upm-tags', query],
    queryFn: async () => {
      try {
        const response = await api.getUpmTags(query);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching tags:', error);
        return [];
      }
    },
    enabled: enabled && query.length >= 3, // Only search when query is at least 3 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Hook for UPM Variant Values search
export const useUpmVariantValues = (categoryId: string, query?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['upm-variant-values', categoryId, query],
    queryFn: async () => {
      try {
        const response = await api.getUpmVariantValues(categoryId, query);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching variant values:', error);
        return [];
      }
    },
    enabled: enabled && !!categoryId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};
